import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Custom Google OAuth using @react-oauth/google's auth-code popup flow.
// The redirect_uri used by Google for popup auth-code exchanges is the
// special string "postmessage" — the backend must pass the same value
// when exchanging the code with Google's token endpoint.
function useCustomGoogleLogin({ onSuccess, onError }) {
  return useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        const { data } = await api.post("/auth/google", {
          code,
          redirect_uri: "postmessage",
        });
        onSuccess(data);
      } catch (err) {
        onError(err);
      }
    },
    onError: (err) => onError(err),
  });
}

function AuthShell({ children, side = "Sign in", title, subtitle }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      {/* Left visual */}
      <div className="relative hidden overflow-hidden bg-neutral-100 lg:col-span-2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1741806914412-340ca16e9175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTc2OTg1MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-white/10" />
        <div className="relative z-10 flex h-full flex-col justify-between p-10 text-black">
          <Link to="/" className="font-serif text-2xl font-bold tracking-tight">
            PostShifted<span className="text-neutral-500">.</span>
          </Link>
          <div>
            <p className="max-w-xs font-serif text-2xl leading-snug tracking-tight">
              &ldquo;One draft. Five channels. Zero rewriting.&rdquo;
            </p>
            <p className="mt-4 text-xs uppercase tracking-widest text-neutral-600">— the remix</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="col-span-1 flex items-center justify-center bg-[#FAFAFA] px-6 py-16 lg:col-span-3">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-12 inline-block font-serif text-xl font-bold lg:hidden">
            PostShifted.
          </Link>
          <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— {side}</p>
          <h1 className="mb-4 font-serif text-5xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="mb-10 text-sm text-neutral-600">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = useCustomGoogleLogin({
    onSuccess: (data) => {
      loginWithToken(data);
      toast.success("Welcome back");
      navigate("/dashboard");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    },
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell side="Sign in" title="Welcome back." subtitle="Pick up where you left off.">
      <Button
        type="button"
        variant="outline"
        onClick={() => googleLogin()}
        data-testid="google-login-btn"
        className="mb-6 h-12 w-full rounded-none border-neutral-300 bg-white text-sm font-medium hover:bg-neutral-100"
      >
        <svg className="mr-3 h-4 w-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-neutral-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase tracking-widest">
          <span className="bg-[#FAFAFA] px-3 text-neutral-500">or</span>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-widest text-neutral-500">Password</Label>
          <Input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="h-12 w-full rounded-[3px] bg-coral text-white hover:bg-coral-hover hover:shadow-coral transition-all duration-300"
        >
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>

      <p className="mt-8 text-sm text-neutral-600">
        No account?{" "}
        <Link to="/signup" data-testid="link-to-signup" className="font-medium text-black underline underline-offset-4">
          Create one
        </Link>
      </p>
      <p className="mt-6 text-xs leading-relaxed text-neutral-500">
        <Link to="/terms" className="text-neutral-700 underline underline-offset-2 hover:text-coral">Terms</Link>
        {" · "}
        <Link to="/privacy" className="text-neutral-700 underline underline-offset-2 hover:text-coral">Privacy</Link>
        {" · "}
        <Link to="/refund" className="text-neutral-700 underline underline-offset-2 hover:text-coral">Refund</Link>
      </p>
    </AuthShell>
  );
}

export function Signup() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const googleSignup = useCustomGoogleLogin({
    onSuccess: (data) => {
      loginWithToken(data);
      toast.success("Account created");
      navigate("/dashboard");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    },
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { name, email, password });
      loginWithToken(data);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell side="Create an account" title="Start remixing." subtitle="5 free remixes every month. No card.">
      <Button
        type="button"
        variant="outline"
        onClick={() => googleSignup()}
        data-testid="google-signup-btn"
        className="mb-6 h-12 w-full rounded-none border-neutral-300 bg-white text-sm font-medium hover:bg-neutral-100"
      >
        <svg className="mr-3 h-4 w-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-neutral-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase tracking-widest">
          <span className="bg-[#FAFAFA] px-3 text-neutral-500">or</span>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-xs uppercase tracking-widest text-neutral-500">Name</Label>
          <Input
            id="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signup-name-input"
            className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="signup-email-input"
            className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-widest text-neutral-500">
            Password <span className="text-neutral-400">(min 6 chars)</span>
          </Label>
          <Input
            id="password"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="signup-password-input"
            className="mt-2 h-12 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="signup-submit-btn"
          className="h-12 w-full rounded-[3px] bg-coral text-white hover:bg-coral-hover hover:shadow-coral transition-all duration-300"
        >
          {loading ? "Creating..." : "Create account"}
        </Button>
      </form>

      <p className="mt-8 text-sm text-neutral-600">
        Have an account?{" "}
        <Link to="/login" data-testid="link-to-login" className="font-medium text-black underline underline-offset-4">
          Sign in
        </Link>
      </p>
      <p
        data-testid="signup-legal-strip"
        className="mt-6 text-xs leading-relaxed text-neutral-500"
      >
        By creating an account you agree to our{" "}
        <Link to="/terms" className="text-neutral-700 underline underline-offset-2 hover:text-coral">Terms</Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-neutral-700 underline underline-offset-2 hover:text-coral">Privacy Policy</Link>.
      </p>
    </AuthShell>
  );
}
